import ConstructorIOClient from '@constructor-io/constructorio-client-javascript';
import camelcaseKeys from 'camelcase-keys';
import cookie from 'cookie';
import { NextPageContext } from 'next';

import { generateCdnImagesBmig } from '../generateCdnImageUrl';

export const cioSortOptions = [
  {
    isDefault: true,
    value: 'relevance',
    sort: 'relevance',
    sortOrder: 'descending',
    defaultMessage: 'Relevance',
  },
  {
    value: 'newest',
    sort: 'newest',
    sortOrder: 'descending',
    defaultMessage: 'New Arrivals',
  },
  {
    value: 'bestsellers',
    sort: 'best_sellers',
    sortOrder: 'descending',
    defaultMessage: 'Best Sellers',
  },
  {
    value: 'pricel2h',
    sort: 'pricel2h',
    sortOrder: 'ascending',
    defaultMessage: 'Price: Low to High',
  },
  {
    value: 'priceh2l',
    sort: 'priceh2l',
    sortOrder: 'descending',
    defaultMessage: 'Price: High to Low',
  },
];

export const defaultCioSortOption =
  cioSortOptions.find(option => option.isDefault) || cioSortOptions[0];

const mapData: [string, string][] = [
  ['size_bra_label', 'size_bra'],
  ['size_bralette_label', 'size_bralette'],
  ['size_undie_label', 'size_undie'],
  ['size_lingerie_label', 'size_lingerie'],
  ['tag_color_label', 'color'],
  ['tag_collection_label', 'collection'],
  ['tag_fabric_label', 'fabric'],
  ['size_label', 'size'],
];

const fieldNameMap = new Map<string, string>([
  ...mapData,
  ...mapData.map<[string, string]>(([a, b]) => {
    return [b, a];
  }),
]);

export const normalizeCioFacetData = facets => {
  return facets
    .map(facet => {
      return {
        field: fieldNameMap.get(facet.name),
        label: facet.display_name,
        totalAggregationCount: facet.options?.length,
        items: facet.options,
      };
    })
    .filter(facet => facet.field !== undefined);
};

export const normalizeCioSearchResults = results => {
  return results
    .map(result => result.data)
    .map(product => ({
      ...camelcaseKeys(product),
      masterProductId: Number(product.id),
      productImages: generateCdnImagesBmig(product),
    }));
};

export const normalizeCioSortOptions = options => {
  return options.map(option => {
    return {
      value: option.sort_by,
      sort: option.sort_by,
      defaultMessage: option.display_name,
      isDefault: option.status === 'selected' ? true : undefined,
    };
  });
};

export const getCioSortBy = (sort: string, isVip: boolean) => {
  switch (sort) {
    case 'pricel2h':
      return isVip ? 'vip_price_l2h' : 'retail_price_l2h';
    case 'priceh2l':
      return isVip ? 'vip_price_h2l' : 'retail_price_h2l';
    default:
      return sort;
  }
};

export const removeCioEmptyFilters = obj => {
  return Object.entries(obj).reduce((entry, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      if (fieldNameMap.get(key)) {
        entry[fieldNameMap.get(key)] = value;
      }
    }
    return entry;
  }, {});
};

export const createExclusionPreFilterExpression = (
  key: string,
  values: number[] = []
) => {
  return values.map(value => {
    return {
      not: {
        name: key,
        value: value.toString(),
      },
    };
  });
};

type CioSearchProductsParams = {
  page: number;
  pageSize: number;
  payload: {
    aggregationFilter: Record<string, number[]>;
    sort: string;
    sortOrder: string;
  };
  searchQuery: string;
  cioClient: ConstructorIOClient;
  excludeFpls: number[];
  excludeTagIds: number[];
  isVip: boolean;
};
export const getCioSearchProducts = async ({
  page,
  pageSize,
  payload,
  searchQuery,
  cioClient,
  excludeFpls,
  excludeTagIds,
  isVip,
}: CioSearchProductsParams) => {
  if (!searchQuery) {
    return [];
  }

  const results = await cioClient.search.getSearchResults(searchQuery, {
    resultsPerPage: pageSize,
    sortBy: getCioSortBy(payload?.sort, isVip),
    sortOrder: payload?.sortOrder,
    page,
    filters: removeCioEmptyFilters(payload.aggregationFilter),
    preFilterExpression: {
      and: [
        ...createExclusionPreFilterExpression(
          'featured_product_location_id_list',
          excludeFpls
        ),
        ...createExclusionPreFilterExpression('tag_id_list', excludeTagIds),
        {
          name: 'date_expected_epoch_timestamp',
          range: ['-inf', new Date().getTime()],
        },
      ],
    },
  });

  const cioResults = normalizeCioSearchResults(results?.response?.results);
  const totalResults = results?.response?.total_num_results;
  const facets = normalizeCioFacetData(results?.response?.facets);
  const sortOptions = normalizeCioSortOptions(results?.response?.sort_options);

  return {
    aggregations: facets,
    page,
    pageSize,
    pages: Math.ceil(totalResults / pageSize),
    products: cioResults,
    sortOptions,
    total: totalResults,
  };
};

export const getCioCollectionProducts = async ({
  page,
  size: pageSize,
  sort,
  aggregationFilter,
  collectionId,
  cioClient,
  excludeFpls,
  excludeTagIds,
  isVip,
}) => {
  const results = await cioClient.browse.getBrowseResults(
    'collection_id',
    collectionId,
    {
      resultsPerPage: pageSize,
      sortBy: getCioSortBy(sort, isVip),
      sortOrder: cioSortOptions.find(option => option.sort === sort)?.sortOrder,
      page,
      filters: removeCioEmptyFilters(aggregationFilter),
      preFilterExpression: {
        and: [
          ...createExclusionPreFilterExpression(
            'featured_product_location_id_list',
            excludeFpls
          ),
          ...createExclusionPreFilterExpression('tag_id_list', excludeTagIds),
          {
            name: 'date_expected_epoch_timestamp',
            range: ['-inf', new Date().getTime()],
          },
        ],
      },
    }
  );

  const cioResults = normalizeCioSearchResults(results?.response?.results);
  const totalResults = results?.response?.total_num_results;
  const facets = normalizeCioFacetData(results?.response?.facets);
  const sortOptions = normalizeCioSortOptions(results?.response?.sort_options);

  return {
    aggregations: facets,
    page,
    pageSize,
    pages: Math.ceil(totalResults / pageSize),
    products: cioResults,
    sortOptions,
    total: totalResults,
  };
};

export const fetchResultsServerSide = async (
  context: NextPageContext,
  apiKey,
  searchQuery
) => {
  const { req } = context;
  const parsedCookies = cookie.parse(
    req ? req.headers.cookie || '' : document.cookie
  );

  // eslint-disable-next-line camelcase
  const { ConstructorioID_client_id, ConstructorioID_session_id = 1 } =
    parsedCookies;

  const fetchResult = await fetch(
    // eslint-disable-next-line camelcase
    `https://ac.cnstrc.com/search/${searchQuery}?key=${apiKey}&num_results_per_page=1&i=${ConstructorioID_client_id}&s=${ConstructorioID_session_id}`
  );

  const resultData = await fetchResult.json();

  return {
    redirectUrl: resultData?.response?.redirect?.data.url,
    facets: normalizeCioFacetData(resultData?.response?.facets || []),
  };
};
